import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";

type OAuthApi = {
  getAuthorizationDetails: (id: string) => Promise<{ data: any; error: { message: string } | null }>;
  approveAuthorization: (id: string) => Promise<{ data: any; error: { message: string } | null }>;
  denyAuthorization: (id: string) => Promise<{ data: any; error: { message: string } | null }>;
};
const oauth = () => (supabase.auth as unknown as { oauth: OAuthApi }).oauth;

export const Route = createFileRoute("/.lovable/oauth/consent")({
  ssr: false,
  staticData: { sitemap: "exclude-subtree" },
  validateSearch: (s: Record<string, unknown>) => ({
    authorization_id: typeof s["authorization_id"] === "string" ? s["authorization_id"] : "",
  }),
  head: () => ({
    meta: [
      { title: "Connect an app | factory.online" },
      { name: "description", content: "Approve an AI assistant's access to your factory.online account." },
      { property: "og:title", content: "Connect an app | factory.online" },
      { property: "og:description", content: "Approve an AI assistant's access to your factory.online account." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Consent,
});

function Consent() {
  const { authorization_id } = Route.useSearch();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [details, setDetails] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSignedIn(!!s));
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!signedIn || !authorization_id) return;
    oauth()
      .getAuthorizationDetails(authorization_id)
      .then(({ data, error }) => {
        if (error) return setError(error.message);
        const immediate = data?.redirect_url ?? data?.redirect_to;
        if (immediate && !data?.client) window.location.href = immediate;
        else setDetails(data);
      });
  }, [signedIn, authorization_id]);

  async function signIn(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: String(f.get("email") ?? ""),
      password: String(f.get("password") ?? ""),
    });
    setBusy(false);
    if (error) setError(error.message);
  }

  async function decide(approve: boolean) {
    setBusy(true);
    const { data, error } = approve
      ? await oauth().approveAuthorization(authorization_id)
      : await oauth().denyAuthorization(authorization_id);
    if (error) { setBusy(false); return setError(error.message); }
    const target = data?.redirect_url ?? data?.redirect_to;
    if (!target) { setBusy(false); return setError("No redirect returned."); }
    window.location.href = target;
  }

  const name = details?.client?.name ?? details?.client?.client_name ?? "An app";

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
        {!authorization_id ? (
          <p>Missing authorization request.</p>
        ) : signedIn === null ? (
          <Loader2 className="mx-auto size-6 animate-spin text-primary" />
        ) : !signedIn ? (
          <form onSubmit={signIn} className="grid gap-4">
            <h1 className="font-display text-2xl font-bold">Sign in to continue</h1>
            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" name="email" type="email" required autoComplete="email" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" name="password" type="password" required autoComplete="current-password" />
            </div>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={busy}>Sign in</Button>
          </form>
        ) : (
          <div className="grid gap-4">
            <h1 className="font-display text-2xl font-bold">Connect {name}</h1>
            <p className="text-sm text-muted-foreground">
              {name} will be able to read site content and work with requests as you.
            </p>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <div className="flex gap-2">
              <Button disabled={busy || !details} onClick={() => decide(true)}>Approve</Button>
              <Button variant="outline" disabled={busy || !details} onClick={() => decide(false)}>Deny</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
